import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Container, Card, CardHeader, ListGroup } from 'reactstrap';
import { observer } from 'mobx-react';

import { stringSearchService } from '@app/Services/StringSearchService';

import { CardBodyLoading } from '@app/Components/Loading';
import { AzureAuth } from '@app/Components/AzureAuth';
import PageErrorBoundary from '@app/Components/PageErrorBoundary';
import JobListStore from './Stores/JobListStore';
import JobItem from './Components/JobItem/JobItem';
import JobListTitle from './Components/JobListTitle/JobListTitle';
import JobListFilter from './Components/JobListFilter/JobListFilter';
import './job-list.scss';

@observer
export default class JobList extends React.Component<RouteComponentProps> {
    private _store: JobListStore;

    constructor(props: RouteComponentProps) {
        super(props);
        this._store = new JobListStore();
    }

    componentDidMount() {
        void this._store.initJobs();
        void this._store.loadQueueNames();
    }

    componentWillUnmount() {
        this._store.unload();
    }

    public render() {
        const isAnyJobExecution = !!this._store.topExecutions.length;

        return (
            <PageErrorBoundary title="Jobs" bodyClassName="container-view">
                <Container className="job-list p-0">
                    <Card>
                        <CardHeader tag="h3" className="d-flex">
                            <JobListTitle showExecutions={isAnyJobExecution} executions={this._store.topExecutions} failedJobs={this._store.topFailedJobs} />
                            {this._store.dataLoaded && <JobListFilter value={this._store.jobsFilter} onChange={(e) => this._store.setFilter(e.target.value)} />}
                        </CardHeader>
                        {this._renderJobs()}
                    </Card>
                </Container>
            </PageErrorBoundary>
        );
    }

    private _renderJobs() {
        if (!this._store.dataLoaded) {
            return <CardBodyLoading className="job-loader" loading />;
        }

        if (!this._store.jobs.length) {
            if (this._store.exceptionMessage) {
                return (
                    <div className="py-3 text-center">
                        <span className="job-list-placeholder job-list-placeholder-error">{this._store.exceptionMessage}</span>
                    </div>
                );
            }
            return (
                <div className="py-3 text-center">
                    <span className="job-list-placeholder">No jobs yet</span>
                </div>
            );
        }

        return (
            <ListGroup flush>
                {this._store.jobs
                    .filter((jobItem) => this._store.filterJob(jobItem))
                    .map(jobItem => (
                        <JobItem key={jobItem.id}
                            job={jobItem}
                            queues={this._store.queues}
                            hasReadAccess={AzureAuth.canSeeGeneralAdmin}
                            hasWriteAccess={AzureAuth.canWriteGeneralAdmin}
                            hasRunAccess={AzureAuth.canRunAdminTools}
                            renderText={(text) => this._store.showFilter ? this._renderText(text) : <>{text}</>}
                            onParametersSaved={(e) => this._store.updateJob(e)}
                            onSettingsSaved={(e) => this._store.updateJob(e)}
                            onTriggerCreated={(e) => this._store.updateJob(e)}
                            onTriggerDeleted={(e) => this._store.updateJob(e)}
                            onJobStarted={(e) => this._store.updateJob(e)} />
                    ))}
            </ListGroup>
        );
    }

    private _renderText(text: string) {
        const { searchTerms, textComparer } = this._store;
        const parts = stringSearchService.markMatches(text, searchTerms, textComparer);

        return (
            <>
                {parts.map((p, index) =>
                    p.isMatch ? (
                        <span className="text-match" key={'t' + index}>
                            {`${p.text}`}
                        </span>
                    ) : (
                        p.text
                    )
                )}
            </>
        );
    }
}
